import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import Chart from 'components/molecules/Chart';
import {
  chartColors,
  chartMax,
  formatDataValue,
  formatTickLabel,
} from 'utilities/chart';
import {
  getItemConcise,
  getItemLabel,
  itemProps,
  NULL_VALUE,
} from 'utilities/item';

const propTypes = {
  displayOptions: PropTypes.arrayOf(
    PropTypes.shape({
      format: PropTypes.string,
      label: PropTypes.string,
      value: PropTypes.string,
    }),
  ).isRequired,
  groupLabels: PropTypes.arrayOf(PropTypes.string).isRequired,
  items: PropTypes.arrayOf(itemProps).isRequired,
  segmentKey: PropTypes.string,
};

const defaultProps = {
  segmentKey: undefined,
};

function getData(items, displayOptions, segmentKey, groupLabels) {
  const dataMap = new Map();
  items.forEach((item) => {
    displayOptions.forEach((option, index) => {
      const {
        format,
        label,
        value,
      } = option;

      groupLabels.forEach((group) => {
        const key = segmentKey ? item[segmentKey]?.value : value;
        const metricKey = `${value}::${group}`;

        const itemData = dataMap.get(key) || {
          color: item?.color?.value || chartColors[index % chartColors.length],
          contents: [],
          ids: [],
          labels: [],
          lineLabel: segmentKey ? getItemLabel(item, segmentKey) : label,
          values: [],
        };

        const formattedValue = formatDataValue(item[metricKey]?.value, format);

        dataMap.set(key, {
          ...itemData,
          contents: [...itemData.contents, getItemConcise(item, metricKey)],
          ids: [...itemData.ids, item.id?.value],
          labels: [...itemData.labels, group],
          values: [...itemData.values, formattedValue],
        });
      });
    });
  });

  return Array.from(dataMap.values());
}

function getChartData(data, displayOptions) {
  const hasMultipleAxes = displayOptions.length > 1;

  return data.reduce((all, lineData, index) => {
    const axisID = hasMultipleAxes ? `y${index}` : 'y0';

    return {
      ...all,
      datasets: [
        ...all.datasets,
        {
          backgroundColor: lineData.color,
          borderColor: lineData.color,
          contents: lineData.contents,
          data: lineData.values,
          hoverBackgroundColor: lineData.color,
          label: lineData.lineLabel,
          pointHitRadius: 5,
          pointRadius: (context) => {
            const idx = context.dataIndex;
            const prevValue = context.dataset.data[idx - 1];
            const nextValue = context.dataset.data[idx + 1];

            if (prevValue === undefined && nextValue === undefined) {
              return 5;
            }

            return 1;
          },
          yAxisID: axisID,
        },
      ],
    };
  }, {
    datasets: [],
    labels: data[0]?.labels || [],
  });
}

function getMaxValue(items, displayKey, groupLabels, format) {
  const maxValue = items.reduce((max, item) => {
    const values = groupLabels.map((group) => {
      const metricKey = `${displayKey}::${group}`;
      const value = item[metricKey]?.value;
      return value !== NULL_VALUE ? value : -Infinity;
    });

    return Math.max(max, ...values);
  }, -Infinity);

  return chartMax(maxValue, format === 'percent');
}

function getChartOptions(items, displayOptions, groupLabels) {
  return {
    indexAxis: 'x',
    layout: {
      padding: {
        right: 50,
      },
    },
    maintainAspectRatio: false,
    plugins: {
      datalabels: {
        display: false,
      },
      legend: {
        labels: {
          boxHeight: 10,
          boxWidth: 10,
          generateLabels: ({ data: { datasets } }) => (
            datasets.map(({ backgroundColor, label }, index) => ({
              borderRadius: 5,
              datasetIndex: index,
              fillStyle: backgroundColor,
              lineWidth: 0,
              text: label,
            }))
          ),
        },
        onClick: () => {},
      },
      tooltip: {
        callbacks: {
          label: ({ dataset, dataIndex }) => dataset.contents[dataIndex],
          title: () => null,
        },
        displayColors: false,
        yAlign: 'bottom',
      },
    },
    scales: {
      ...displayOptions.reduce((all, {
        format,
        label,
        value,
      }, index) => {
        const axisId = `y${index}`;
        const position = index === 0 ? 'left' : 'right';

        return {
          ...all,
          [axisId]: {
            beginAtZero: true,
            border: {
              display: false,
            },
            grid: {
              drawTicks: true,
            },
            max: getMaxValue(items, value, groupLabels, format),
            position,
            ticks: {
              callback: (val) => formatTickLabel(val, format),
              count: 5,
            },
            title: {
              display: true,
              text: label,
            },
          },
        };
      }, {}),
      x: {
        border: {
          display: true,
        },
        grid: {
          display: true,
        },
        offset: groupLabels.length < 3,
        ticks: {
          rotation: 45,
        },
      },
    },
  };
}

function ItemsLineChart({
  displayOptions,
  items,
  segmentKey,
  groupLabels,
}) {
  return useMemo(() => {
    // @note: only recreate the chart when data changes so it doesn't re-run the animation
    const data = getData(items, displayOptions, segmentKey, groupLabels);
    const chartData = getChartData(data, displayOptions);
    const chartOptions = getChartOptions(items, displayOptions, groupLabels);

    return (
      <Chart
        data={chartData}
        options={chartOptions}
        type="line"
      />
    );
  }, [
    displayOptions,
    items,
    segmentKey,
    groupLabels,
  ]);
}

ItemsLineChart.propTypes = propTypes;
ItemsLineChart.defaultProps = defaultProps;

export default ItemsLineChart;
